/**
 * ชนิดข้อมูลของการนำเข้า Forecast ลูกค้า (Forecast Import) — เขียนตาม payload
 * contract ที่ freeze แล้ว (`docs/API_CONTRACT.md` — "Forecast Import
 * (`PLANNING`) — Phase 2", D-093)
 *
 * กลุ่มนี้เป็นงาน **สองเฟส** เหมือน Bulk Initial Stock Import (D-080): เฟสแรกคือ
 * การนำไฟล์ของลูกค้าเข้ามา **แล้ว validate ทั้งชุด** ซึ่งเขียนทุกบรรทัดลงฐานข้อมูล
 * รวมบรรทัดที่ผิด และเฟสที่สองคือการ **Publish** ที่มีคนตรวจแล้วกดอย่างจงใจ
 *
 * สามข้อที่กำหนดทั้งหน้าจอ และทุกข้อมาจาก contract ไม่ใช่รสนิยม:
 *
 * 1. **บรรทัดที่ผิดถูกเก็บไว้ ไม่ใช่ถูกทิ้ง** — batch ที่มีบรรทัดผิดได้สถานะ
 *    `INVALID` และแต่ละบรรทัดที่ผิดถือ `validation_error` เป็น **rule token**
 *    หนึ่งตัว (flow ข้อ 1.3) หน้าจอจึงต้องบอกได้ว่า **แถวไหน** ผิดและ **เพราะอะไร**
 *    ไม่ใช่แค่ว่า "นำเข้าไม่สำเร็จ"
 * 2. **สร้างไม่ใช่ publish** — `POST` คืน batch พร้อมสถานะและบรรทัดครบ แต่ยังไม่มี
 *    demand ใดเกิดขึ้น จนกว่าจะมีคนกด Publish ซึ่งเป็นสิทธิ์ APPROVE คนละตัวกับ
 *    CREATE (flow ข้อ 1.4 — "ผู้รับผิดชอบตรวจและ Publish")
 * 3. **Publish ไม่เขียนทับของเดิม** — batch ที่ `PUBLISHED` อยู่ก่อนของคู่ (โรงงาน,
 *    ลูกค้า) เดียวกันกลายเป็น `SUPERSEDED` และ `superseded_by_batch_id` ชี้มาที่ใบใหม่
 *    ใบเก่า**ยังอยู่และยังเปิดอ่านได้** เพราะประวัติ forecast คือสิ่งที่ฝ่ายวางแผนใช้
 *    ตอบว่าทำไมแผนเดือนที่แล้วถึงเป็นอย่างนั้น
 *
 * ⚠️ **contract เขียนตัวอย่างของ `POST` body ไว้ทีละฟิลด์ แต่ไม่ได้เขียนรูป response
 * ไว้ทีละฟิลด์** (เหมือนกลุ่มใบสั่งขาย) ชนิดข้อมูลข้างล่างจึง **บังคับเฉพาะฟิลด์ที่
 * contract เอ่ยชื่อไว้ตรง ๆ** และปล่อยฟิลด์ประกอบ (UUID คู่ของรหัส ผู้กระทำ และเวลา)
 * เป็น nullable ที่หายไปทั้งคีย์ได้ — ดูเหตุผลเต็มที่ `parseForecastImportSummary()`
 * ใน `client.ts`
 */

/**
 * รหัสโมดูลของหน้าจอนี้ใน RBAC matrix
 *
 * โมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration — D-060 จึง deny ADMIN และ
 * ฝ่ายวางแผนได้รับมันผ่านตารางสิทธิ์เหมือนโมดูลธุรกิจตัวอื่นทุกตัว
 *
 * **โมดูลใหม่ ไม่ใช่ `SALES` หรือ `PROCUREMENT` ที่ใช้ซ้ำ** (D-093): Forecast/MPS/MRP
 * เป็นงานของฝ่ายวางแผน ซึ่งไม่ใช่ทั้งการขายและการซื้อ — forecast คือสิ่งที่โรงงาน
 * วางแผนไว้ **ก่อน** ที่ใครจะสั่งอะไร
 */
export const PLANNING_MODULE = 'PLANNING';

/* ------------------------------------------------------------- สถานะ ----- */

/** ผ่านการตรวจทุกบรรทัด — **ใบเดียวเท่านั้นที่ publish ได้** */
export const VALIDATED_BATCH_STATUS = 'VALIDATED';
/** มีอย่างน้อยหนึ่งบรรทัดที่ผิด — ทุกบรรทัดถูกเก็บไว้ครบ แต่ publish ไม่ได้ */
export const INVALID_BATCH_STATUS = 'INVALID';
/** ผ่านคนตรวจแล้ว — เป็น forecast ที่ระบบเอาไปใช้เป็น demand จริง */
export const PUBLISHED_BATCH_STATUS = 'PUBLISHED';
/** ถูกแทนที่ด้วย revision ใหม่ของคู่ (โรงงาน, ลูกค้า) เดียวกัน — **ไม่ได้ถูกลบ** */
export const SUPERSEDED_BATCH_STATUS = 'SUPERSEDED';
/** ถูกยกเลิก */
export const CANCELLED_BATCH_STATUS = 'CANCELLED';

/**
 * ห้าค่าที่ CHECK constraint ฝั่งเซิร์ฟเวอร์รับ (D-093) ตามลำดับของ lifecycle
 *
 * **`DRAFT` ไม่อยู่ในชุดนี้** แม้ flow ข้อ 1 จะเอ่ยถึง: contract ของ P2-5a ทำให้
 * `POST` ตัดสินผลทันที batch จึงเกิดมาเป็น `VALIDATED` หรือ `INVALID` เสมอ
 * ไม่มีช่วงเวลาใดที่มันเป็นร่างที่ยังไม่ถูกตรวจ
 *
 * `CANCELLED` **ยังไม่มี endpoint ใดเขียนมันใน P2-5a** แต่อยู่ในชุดนี้เพราะหน้าจอ
 * ต้องอ่านมันออกเมื่อวันที่มีคนเขียนมาถึง — รูปแบบเดียวกับที่ `SalesOrderStatus`
 * ประกาศสามสถานะของ MRP/งานส่งของไว้ล่วงหน้า
 */
export const FORECAST_BATCH_STATUSES = [
  VALIDATED_BATCH_STATUS,
  INVALID_BATCH_STATUS,
  PUBLISHED_BATCH_STATUS,
  SUPERSEDED_BATCH_STATUS,
  CANCELLED_BATCH_STATUS,
] as const;

export type ForecastBatchStatus = (typeof FORECAST_BATCH_STATUSES)[number];

export function isForecastBatchStatus(value: string): value is ForecastBatchStatus {
  return (FORECAST_BATCH_STATUSES as readonly string[]).includes(value);
}

/* -------------------------------------------------------- ข้อจำกัดความยาว -- */

/**
 * `version_label` ≤30 (D-093) — **ชื่อ revision ที่ลูกค้าให้มา** เช่น `2026-W38-rev2`
 *
 * ไม่ใช่เลขเอกสารของเราเอง และไม่ใช่ unique key: ลูกค้ารายเดียวส่งไฟล์ซ้ำได้เรื่อย ๆ
 * ตาม flow ข้อ 1.4 การกันซ้ำจึงเป็นเรื่องของ supersede ไม่ใช่ของ label นี้
 */
export const VERSION_LABEL_MAX_LENGTH = 30;

/** `source_file_hash` ≤64 (D-093) — เก็บไว้เพื่อ audit และ **ไม่ใช่ idempotency key** */
export const SOURCE_FILE_HASH_MAX_LENGTH = 64;

/** รหัสอ้างอิงที่ฟอร์มกรอก — ความยาวเดียวกับคอลัมน์ของเจ้าของข้อมูลนั้น ๆ */
export const PLANT_CODE_MAX_LENGTH = 20;
export const CUSTOMER_CODE_MAX_LENGTH = 20;
export const PRODUCT_CODE_MAX_LENGTH = 40;
export const UOM_CODE_MAX_LENGTH = 10;

/* ------------------------------------------------- กฎระดับคำขอ (ทั้ง batch) -- */

/**
 * `422` — **batch นี้มีบรรทัดที่ผิด จึง publish ไม่ได้**
 *
 * กฎที่แพงที่สุดของกลุ่มนี้ และเป็น backstop ของสิ่งที่หน้าจอกันไว้แล้วด้วยการไม่
 * เสนอปุ่ม Publish ให้ใบที่ `INVALID` เลย (flow ข้อ 1.3 — แถวที่ผิดห้ามสร้าง demand
 * เงียบ ๆ) ทางแก้คือแก้ไฟล์แล้วนำเข้าใหม่ ไม่ใช่กดซ้ำและไม่ใช่ขอสิทธิ์เพิ่ม
 */
export const FORECAST_BATCH_IS_INVALID = 'FORECAST_BATCH_IS_INVALID';

/**
 * `409` — ใบนี้ไม่ได้อยู่ที่ `VALIDATED` จึง publish ไม่ได้ (พร้อม
 * `details.current_status`)
 *
 * **คนละเรื่องกับ `FORECAST_BATCH_IS_INVALID`**: ใบที่ `PUBLISHED` แล้ว หรือถูก
 * supersede ไปแล้ว ไม่ได้ "ผิด" อะไรเลย มันแค่ผ่านจุดนั้นไปแล้ว
 */
export const FORECAST_BATCH_NOT_VALIDATED = 'FORECAST_BATCH_NOT_VALIDATED';

/** `403` — โรงงานที่กรอกอยู่นอก `plant_scope` ของผู้เรียก (D-004) */
export const PLANT_OUTSIDE_SCOPE = 'PLANT_OUTSIDE_SCOPE';

/** `422` — ไม่มีลูกค้ารหัสนี้ในทะเบียน */
export const CUSTOMER_NOT_FOUND = 'CUSTOMER_NOT_FOUND';

/** `400` — สองบรรทัดในคำขอเดียวกันอ้างเลขบรรทัดเดียวกัน */
export const DUPLICATE_LINE_NO = 'DUPLICATE_LINE_NO';

/**
 * `400` — body มีฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของ (`status`, `batch_id`, ผู้กระทำ, เวลา)
 *
 * หน้าจอนี้ประกอบ body ทีละคีย์ (`serializeCreateForecastImportBody()`) จึงไม่มีทาง
 * ส่งฟิลด์เหล่านั้นไปได้เลย — การที่คำตอบนี้มาถึงคือบั๊ก ไม่ใช่สิ่งที่ผู้ใช้แก้ได้
 */
export const FORECAST_IMPORT_FIELD_IS_SERVER_OWNED = 'FORECAST_IMPORT_FIELD_IS_SERVER_OWNED';

/* ----------------------------------------------- กฎระดับบรรทัด (ในไฟล์) ---- */

/**
 * ห้า token ที่ลงมาอยู่ใน `forecast_line.validation_error` ได้ — **คนละชุดกับกฎ
 * ระดับคำขอข้างบน**
 *
 * ความต่างนี้เป็นหัวใจของกลุ่มนี้: กฎข้างบนทำให้ **ทั้งคำขอ** ถูกปฏิเสธและไม่มีอะไร
 * ถูกเขียน ส่วนห้าตัวนี้ถูก **เก็บลงฐานข้อมูลพร้อมกับแถวที่ผิด** แล้วตอบกลับมาเป็น
 * `201` — บรรทัดที่ผิดอยู่ในระบบให้คนอ่านและแก้ ไม่ได้หายไป
 */

/** รหัสสินค้านี้ไม่มีอยู่ในทะเบียน — บรรทัดจึง resolve `product_id` ไม่ได้เลย */
export const PRODUCT_NOT_FOUND = 'PRODUCT_NOT_FOUND';

/**
 * คู่ (สินค้า, วันที่) นี้ถูกอ้างไปแล้วโดยบรรทัดก่อนหน้าใน batch เดียวกัน
 * (flow ข้อ 1.2)
 */
export const DUPLICATE_FORECAST_LINE = 'DUPLICATE_FORECAST_LINE';

/** มีสินค้ารหัสนี้ แต่ยังไม่ `ACTIVE` (ยังไม่ผ่านอนุมัติ หรือถูกเลิกใช้แล้ว) */
export const PRODUCT_NOT_ACTIVE = 'PRODUCT_NOT_ACTIVE';

/**
 * จำนวนติดลบ — **ไม่ใช่ "ต้องมากกว่าศูนย์"**
 *
 * forecast ของวันหนึ่งที่เป็นศูนย์เป็นคำพูดที่มีความหมายจริง ("วันนั้นเราคาดว่าไม่
 * ต้องใช้เลย") ต่างจากบรรทัดสต๊อกตั้งต้นที่ศูนย์หน่วยแปลว่าไม่ได้รับอะไรเลย ขอบเขต
 * จึงเป็น `>= 0` ที่นี่ และ token ก็บอกความต่างนั้นด้วยตัวมันเอง (D-093)
 */
export const QUANTITY_MUST_NOT_BE_NEGATIVE = 'QUANTITY_MUST_NOT_BE_NEGATIVE';

/** หน่วยนับที่บรรทัดนี้ระบุไม่ใช่หน่วยที่ระบบนี้รู้จัก */
export const UOM_NOT_FOUND = 'UOM_NOT_FOUND';

/**
 * ห้า token ข้างบนรวมเป็นชุดเดียว — ใช้ยืนยันว่าหน้าจอแปลได้ครบทุกตัว
 *
 * ลำดับคือลำดับที่เซิร์ฟเวอร์ตรวจจริง (resolve รหัส → ซ้ำภายใน batch → สถานะสินค้า →
 * จำนวน → หน่วยนับ) และ **บรรทัดหนึ่งถือได้ทีละหนึ่ง token เท่านั้น**: บรรทัดถูกแก้
 * ที่ไฟล์ การไล่บอกทุกวิธีที่แถวเดียวผิดจึงเป็นคำตอบที่ยาวขึ้นสำหรับการแก้ครั้งเดียวกัน
 */
export const FORECAST_LINE_VALIDATION_ERRORS = [
  PRODUCT_NOT_FOUND,
  DUPLICATE_FORECAST_LINE,
  PRODUCT_NOT_ACTIVE,
  QUANTITY_MUST_NOT_BE_NEGATIVE,
  UOM_NOT_FOUND,
] as const;

export type ForecastLineValidationError =
  (typeof FORECAST_LINE_VALIDATION_ERRORS)[number];

/* ------------------------------------------------------------- resource --- */

/**
 * หนึ่งบรรทัดที่ถูกเก็บไว้ — **สิ่งที่ไฟล์บอก และสิ่งที่เกิดขึ้นกับมัน**
 *
 * `product_code` เป็น `string | null` **โดยตั้งใจ** ไม่ใช่เพราะหละหลวม: มันคือสิ่งที่
 * เซิร์ฟเวอร์ **resolve ได้** รหัสที่ resolve ไม่ได้ (`PRODUCT_NOT_FOUND`) จึงไม่มี
 * อะไรให้แสดง — สิ่งที่ระบุแถวนั้นกลับไปยังไฟล์ที่ต้องแก้คือ `line_no` ไม่ใช่รหัสสินค้า
 *
 * `forecast_date` เป็น **วันตามปฏิทินในรูป `YYYY-MM-DD` ล้วน ๆ** ไม่ใช่ instant
 * (D-093) — คอลัมน์ฝั่งเซิร์ฟเวอร์เป็น `DATE` เพราะ forecast คือปริมาณของ *วัน*
 * ไม่ใช่ของ *ชั่วขณะ* ค่านี้จึงห้ามถูกแปลงผ่าน `Date` ทั้งขาส่งและขาแสดง
 *
 * `quantity` เป็น **สตริง** — `NUMERIC(18, 4)` กว้างกว่าที่ IEEE-754 double เก็บได้ครบ
 */
export interface ForecastLine {
  /** เลขบรรทัดในไฟล์ — ตัวระบุแถวที่ผู้ใช้ต้องกลับไปแก้ */
  readonly line_no: number;
  readonly product_id: string | null;
  /** `null` เมื่อรหัสในไฟล์ resolve ไม่ได้ — ดูหมายเหตุของ interface นี้ */
  readonly product_code: string | null;
  /** `YYYY-MM-DD` ล้วน ไม่มีเวลาและไม่มี offset */
  readonly forecast_date: string;
  /** ทศนิยมที่เดินทางมาเป็นสตริงเสมอ — ห้ามแปลงเป็น number */
  readonly quantity: string;
  readonly uom_code: string;
  /** rule token หนึ่งตัว หรือ `null` เมื่อบรรทัดนี้ผ่าน */
  readonly validation_error: string | null;
}

/**
 * หัว batch หนึ่งใบ **โดยไม่มีบรรทัด** — รูปของแต่ละ item ใน
 * `GET /v1/forecast-imports`
 *
 * ไม่มีคีย์ `lines` อยู่ในชนิดนี้เลย **โดยตั้งใจ**: contract ตัดบรรทัดออกจากรายการ
 * (ไฟล์ forecast หนึ่งไฟล์มีหลายร้อยบรรทัด หน้ารายการที่แบกทุกบรรทัดของทุกใบคือ
 * payload ที่ไม่มีใครอ่าน) หน้าจอที่ต้องการบรรทัดจึงต้องเรียก `fetchForecastImport()`
 * ของใบนั้น และ TypeScript เป็นผู้พิสูจน์ว่าไม่มีใครเผลออ่านบรรทัดจากแถวในรายการ
 */
export interface ForecastImportBatchSummary {
  /** UUID surrogate ที่เซิร์ฟเวอร์สร้าง (D-039) — ใช้เป็น path ของ publish */
  readonly batch_id: string;
  readonly plant_id: string | null;
  readonly plant_code: string;
  readonly customer_id: string | null;
  readonly customer_code: string;
  /** ชื่อ revision ที่ลูกค้าให้มา */
  readonly version_label: string;
  readonly status: string;
  readonly source_file_hash: string | null;
  /** จำนวนบรรทัดในไฟล์ — `null` เมื่อเซิร์ฟเวอร์ไม่ได้ส่งมา ไม่ใช่ศูนย์ */
  readonly line_count: number | null;
  readonly created_by_employee_id: string | null;
  readonly published_by_employee_id: string | null;
  readonly published_at: string | null;
  /**
   * ใบที่มาแทนใบนี้ — `null` บนใบที่ยังไม่ถูกแทน
   *
   * บนใบที่ `SUPERSEDED` ค่านี้คือคำตอบของคำถาม "แล้วแผนที่ใช้อยู่ตอนนี้คือใบไหน"
   */
  readonly superseded_by_batch_id: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

/**
 * batch หนึ่งใบ **พร้อมบรรทัดครบ** — รูปของ `GET /{batch_id}`, ของ `201` ที่ `POST`
 * คืนมา และของ `200` ที่ publish คืนมา
 *
 * บรรทัดเดินทางมากับ batch ไม่ใช่หลังคำขอที่สอง เพราะคำถามสองข้อที่ผู้ใช้ถาม —
 * "ใบนี้ publish ได้ไหม" และ "แถวไหนผิด" — เป็นคำถามเดียวกัน: `status` ตอบข้อแรก
 * และ `lines[].validation_error` ตอบข้อที่สอง
 */
export interface ForecastImportBatch extends ForecastImportBatchSummary {
  readonly lines: readonly ForecastLine[];
}

/** `GET /v1/forecast-imports` — envelope มาตรฐานของทุกกลุ่ม ที่ item ไม่มีบรรทัด */
export interface ForecastImportListPage {
  readonly items: readonly ForecastImportBatchSummary[];
  readonly next_cursor: string | null;
}

/* -------------------------------------------------------------- requests -- */

/**
 * หนึ่งบรรทัดที่ฟอร์มส่งออกไป — ห้าคีย์ตามตัวอย่างใน contract และไม่มีคีย์ที่หก
 *
 * **ไม่มี `validation_error` และไม่มี `product_id`**: คำตัดสินและการ resolve เป็นของ
 * เซิร์ฟเวอร์ทุกเส้นทาง และ `extra = "forbid"` ฝั่งนั้นจะตอบ `400` ให้กับบรรทัดที่
 * พยายามส่งมันมา
 */
export interface CreateForecastImportLine {
  /** เลขบรรทัดในไฟล์ของลูกค้า — ห้ามซ้ำกันเองในคำขอเดียว */
  readonly line_no: number;
  readonly product_code: string;
  /** `YYYY-MM-DD` ล้วน — instant ที่มี `T` หรือ offset ถูกปฏิเสธที่เซิร์ฟเวอร์ */
  readonly forecast_date: string;
  /** สตริงเสมอ — `NUMERIC(18, 4)` กว้างกว่าที่ IEEE-754 double เก็บได้ครบ */
  readonly quantity: string;
  readonly uom_code: string;
}

/**
 * body ของ `POST /v1/forecast-imports` — **หัวใบและทุกบรรทัดในคำขอเดียว**
 *
 * **มีแต่ code ไม่มี UUID** ตามคอนเวนชันข้ามกลุ่มของ contract — ผู้ใช้กรอกรหัสที่เขา
 * อ่านออกจากไฟล์ของลูกค้า เซิร์ฟเวอร์เป็นผู้แปลงเป็น id
 *
 * ฟิลด์ที่ contract เรียกว่าเป็นของเซิร์ฟเวอร์ (`batch_id`, `status`, `line_count`,
 * ผู้สร้าง/ผู้ publish และ timestamp ทั้งหมด) ไม่ปรากฏที่นี่เลยแม้แต่คีย์เดียว —
 * ไม่ใช่แค่ "ไม่ส่ง" แต่ **ไม่มีที่ให้ใส่**
 */
export interface CreateForecastImportBody {
  readonly plant_code: string;
  readonly customer_code: string;
  readonly version_label: string;
  /** ไม่บังคับ — คีย์นี้หายไปทั้งคีย์เมื่อผู้ใช้ไม่ได้กรอก */
  readonly source_file_hash?: string;
  /** อย่างน้อยหนึ่งบรรทัด — forecast ที่ไม่มีบรรทัดเลยไม่ใช่ forecast */
  readonly lines: readonly CreateForecastImportLine[];
}
